import React, { useEffect, useState } from "react";

const OTPInput = ({
  length = 6,
  initialValue = [],
  onChange,
  onComplete,
  hasError = false,
  disabled = false,
  autoFocus = true,
}) => {
  const [values, setValues] = useState(
    initialValue.length === length ? initialValue : Array(length).fill("")
  );

  // Store input elements in a mutable ref object using a simple object instead of array
  const inputElements = React.useRef({});

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && inputElements.current[0]) {
      inputElements.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    // Notify parent component
    onChange(newValues);

    // Auto-focus next input if value is provided
    if (
      value !== "" &&
      index < length - 1 &&
      inputElements.current[index + 1]
    ) {
      inputElements.current[index + 1].focus();
    }

    // Check if OTP is complete
    if (newValues.every((v) => v !== "") && onComplete) {
      onComplete(newValues.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    // Move focus to previous input on backspace if current input is empty
    if (
      e.key === "Backspace" &&
      index > 0 &&
      values[index] === "" &&
      inputElements.current[index - 1]
    ) {
      inputElements.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      ?.getData("text")
      ?.replace(/\D/g, "")
      ?.slice(0, length);

    const newValues = [...values];

    [...pastedData].forEach((digit, index) => {
      if (index < length) {
        newValues[index] = digit;
      }
    });

    setValues(newValues);
    onChange(newValues);

    // Focus the next empty input or the last input if all are filled
    const nextEmptyIndex = newValues.findIndex((digit) => digit === "");
    const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;

    if (inputElements.current[focusIndex]) {
      inputElements.current[focusIndex].focus();
    }
    // Check if OTP is complete after paste
    if (newValues.every((v) => v !== "") && onComplete) {
      onComplete(newValues.join(""));
    }
  };

  return (
    <div className="flex w-full gap-2 md:gap-[17px]">
      {values.map((value, index) => (
        <div key={index} className="flex items-center">
          <input
            ref={(el) => {
              if (el) inputElements.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength="1"
            value={value}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            disabled={disabled}
            className={`w-[38px] lg:w-[44px] 2xl:w-[64px] h-12 text-center border rounded-md 
              text-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
              ${hasError ? "border-red-500" : ""}
              ${disabled ? "bg-gray-100 text-gray-400" : ""}
            `}
            aria-label={`OTP digit ${index + 1}`}
          />

          {index === Math.floor(length / 2) - 1 && (
            <div className="hidden 2xl:block pl-[22px] text-greyish w-[43px]">
              <div className="w-[16px] h-[2px] border"></div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Reusable Timer Hook
 */
const useResendTimer = (initialTime = 180, onExpire = () => {}) => {
  const [timeLeft, setTimeLeft] = useState(initialTime);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (timeLeft === 0) {
      onExpire();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, onExpire]);

  // Reset timer function
  const resetTimer = () => {
    setTimeLeft(initialTime);
  };

  const TimerDisplay = () => (
    <div className="text-greyish text-[14px] leading-[20px] mb-5">
      {isPending ? (
        "Sending OTP..."
      ) : (
        <>
          Expires in{" "}
          <span className="text-blueish">{Math.floor(timeLeft)}s</span>
        </>
      )}
    </div>
  );

  return {
    timeLeft,
    resetTimer,
    isPending,
    setIsPending,
    TimerDisplay,
  };
};

export { OTPInput, useResendTimer };
